import { Box, Button, Stack, Tooltip, Typography, styled as muiStyled } from '@mui/material';
import { ArrowDropDown, ArrowForwardIos } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import { abyssColors } from '@/theme/colors';
import { DatePickerIcon } from '@/assets/icons/DatePickerIcon';

export const Container = muiStyled(Stack, {
  shouldForwardProp: (prop) => prop !== 'navbarHeight',
})<{ navbarHeight: number }>(({ navbarHeight }) => ({
  display: 'flex',
  height: `calc(100vh - ${navbarHeight}px)`,
  width: '100%',
  flexDirection: 'column',
  position: 'relative',
  marginTop: `${navbarHeight}px`,
  paddingBottom: 0,
  overflow: 'hidden',
}));
export const ReportSummaryLabel = muiStyled(Typography)(() => ({
  color: abyssColors.primary[400],
  fontSize: '1.2rem',
  marginRight: '4px',
}));

export const ReportSummaryValue = muiStyled(Typography)(() => ({
  color: abyssColors.primary[500],
  fontSize: '1.4rem',
  fontWeight: 500,
}));

export const SummaryLineBreak = muiStyled(Box)(() => ({
  height: '26px',
  width: '1px',
  margin: '0 16px',
  backgroundColor: abyssColors.primary[300],
  opacity: 0.2,
}));

export const NavigationContainer = muiStyled(Stack, {
  shouldForwardProp: (prop) => prop !== 'navbarHeight',
})<{ navbarHeight: number }>(({ navbarHeight }) => ({
  flexDirection: 'column',
  width: '260px',
  height: `calc(100vh - ${navbarHeight + 116}px)`,
  borderRight: `1px solid ${abyssColors.primary[200]}`,
  paddingTop: '16px',
  paddingRight: '8px',
  overflowY: 'scroll',
  msOverflowStyle: 'none',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
}));

export const Header = muiStyled(Stack)(() => ({
  height: '116px',
  padding: '24px',
  paddingTop: '15px',
  borderBottom: `1px solid ${abyssColors.primary[200]}`,
  gap: '20px',
}));

export const EquipmentLineBreak = muiStyled(Box)(() => ({
  height: '1px',
  width: '100%',
  margin: '32px 0px',
  backgroundColor: abyssColors.primary[200],
}));

interface NavigationProps {
  selected?: boolean;
}

export const EquipmentName = muiStyled(Typography)(({ selected }: NavigationProps) => ({
  color: selected ? abyssColors.primary[500] : abyssColors.primary[400],
  fontSize: '1.4rem',
  fontWeight: 700,
  letterSpacing: '0.14px',
  cursor: 'pointer',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

export const POIName = muiStyled(Typography)(({ selected }: NavigationProps) => ({
  color: selected ? abyssColors.primary[500] : abyssColors.primary[400],
  fontWeight: selected ? 700 : 400,
  fontSize: '1.2rem',
  letterSpacing: '0.12px',
  marginLeft: '8px',
  cursor: 'pointer',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  flex: 1,
  minWidth: 0,
}));

export const SectionTitle = muiStyled(POIName)(() => ({
  margin: '16px 0px',
  marginLeft: '32px',
  cursor: 'default',
}));

export const SectionItem = muiStyled(POIName)(() => ({
  marginLeft: '32px',
}));

export const CurrentItemIcon = muiStyled(ArrowForwardIos)(({ selected }: NavigationProps) => ({
  color: abyssColors.primary[500],
  fontSize: '1.8rem',
  visibility: selected ? 'visible' : 'hidden',
}));

export const InputLabel = muiStyled(Typography)(() => ({
  color: abyssColors.primary[400],
  fontSize: '1.2rem',
  marginRight: '4px',
}));

export const ReportDatePicker = muiStyled(DatePicker)(() => ({
  width: '135px',
  '& fieldset': {
    borderColor: `${abyssColors.primary[500]} !important`,
  },
  '& input': {
    padding: '4px 6px',
    paddingRight: '0px',
    fontSize: '1.2rem',
    color: abyssColors.primary[500],
    fontWeight: 500,
    lineHeight: '16px',
    height: '18px',
  },
  '& input::placeholder': {
    fontSize: '1.2rem',
    fontWeight: 500,
    lineHeight: '16px',
    height: '18px',
  },
  '& .MuiInputBase-root ': {
    paddingLeft: '8px',
    paddingRight: '18px',
  },
  borderRadius: '4px',
  padding: 0,
}));

export const datePickerSlotProps = {
  textField: {
    placeholder: 'Set date',
    InputProps: {
      startAdornment: <DatePickerIcon />,
    },
  },
  openPickerButton: {
    disableRipple: true,
    sx: {
      color: abyssColors.primary[500],
      height: '16px',
      width: '16px',
    },
  },
  openPickerIcon: {
    sx: {
      borderLeft: `1px solid ${abyssColors.primary[500]}`,
      height: '16px',
      zoom: 1.2,
      padding: '0px',
      width: 'fit-content',
      paddingLeft: '1px',
    },
  },
};

export const StatusContainer = muiStyled(Stack)(() => ({
  paddingLeft: '8px',
  paddingRight: '0px',
  borderRight: `1px solid ${abyssColors.primary[500]}`,
  marginRight: '0px',
  height: '26px',
  width: '135px',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
}));

export const StatusLabel = muiStyled(Typography)(() => ({
  color: abyssColors.primary[500],
  fontSize: '1.2rem',
  marginRight: '6px',
  lineHeight: 'normal',
  fontWeight: 500,
}));

export const EndAdormentContainer = muiStyled(Stack)(() => ({
  height: '24px',
  width: '24px',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
}));

export const Divider = muiStyled(Box)(() => ({
  width: '1px',
  height: '18px',
  backgroundColor: abyssColors.primary[500],
}));

export const StyledDropDownIcon = muiStyled(ArrowDropDown)(() => ({
  margin: '2px',
  width: '16px',
  zoom: 1.2,
  marginLeft: '1px',
  color: abyssColors.primary[500],
}));

export const EquipmentsContainer = muiStyled(Box, {
  shouldForwardProp: (prop) => prop !== 'navbarHeight',
})<{ navbarHeight: number }>(({ navbarHeight }) => ({
  height: `calc(100vh - ${navbarHeight + 116}px)`,
  overflow: 'auto',
  msOverflowStyle: 'none',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
  padding: '32px',
  width: '100%',
}));

export const LoaderContainer = muiStyled(Stack)(() => ({
  borderRadius: '4px',
  border: `1px solid ${abyssColors.primary[500]}`,
  height: '26px',
  width: '135px',
  alignItems: 'center',
  justifyContent: 'center',
}));

export const NotificationText = muiStyled(Typography)(() => ({
  color: abyssColors.neutral[50],
  fontSize: '1.4rem',
  lineHeight: 'normal',
  fontWeight: 500,
}));

export const NotificationAction = muiStyled(Button)(() => ({
  backgroundColor: abyssColors.neutral[50],
  color: abyssColors.primary[500],
  textTransform: 'none',
  fontSize: '1.2rem',
  fontWeight: 600,
  padding: '4px 14px',
  height: '26px',
  alignItems: 'center',
  lineHeight: 'normal',
  '&:hover': {
    backgroundColor: abyssColors.neutral[200],
  },
}));

export const EquipmentRowStack = muiStyled(Stack)(() => ({
  position: 'relative',
  width: '100%',
  overflow: 'hidden',
  '.drag-handle': {
    opacity: 0,
    transition: 'opacity 0.1s',
    zIndex: 10,
    flexShrink: 0,
  },
  '&:hover .drag-handle': {
    opacity: 1,
  },
  '.delete-btn': {
    opacity: 0,
    transition: 'opacity 0.1s',
    flexShrink: 0,
  },
  '&:hover .delete-btn': {
    opacity: 1,
  },
}));

export const PoiRowStack = muiStyled(Stack)(() => ({
  width: '100%',
  position: 'relative',
  overflow: 'hidden',
  '.drag-handle': {
    opacity: 0,
    transition: 'opacity 0.1s',
    zIndex: 10,
    flexShrink: 0,
  },
  '&:hover .drag-handle': {
    opacity: 1,
  },
  '.delete-btn': {
    opacity: 0,
    transition: 'opacity 0.1s',
    flexShrink: 0,
  },
  '&:hover .delete-btn': {
    opacity: 1,
  },
}));

export const NavigatorTooltip = muiStyled(
  ({ className, ...props }: React.ComponentProps<typeof Tooltip>) => (
    <Tooltip {...props} classes={{ popper: className }} />
  )
)(() => ({
  '& .MuiTooltip-tooltip': {
    backgroundColor: '#224063',
    color: '#fff',
    fontSize: '1.2rem',
  },
}));

export const notificationAlertStyles = {
  backgroundColor: abyssColors.primary[400],
  height: '54px',
  alignItems: 'center',
  '& .MuiAlert-action': {
    padding: 0,
    paddingLeft: '24px',
    paddingRight: '8px',
  },
  '& .MuiAlert-icon': {
    color: abyssColors.neutral[50],
  },
};
